import React, { useEffect, useState } from 'react';
import img1 from '../../Image/AdminPro.svg';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TextField, InputAdornment, CircularProgress, Skeleton } from '@mui/material';
import { User, Mail, Phone, MapPin, Pencil, Save, X, Map, Home } from 'lucide-react';

const Profile = () => {
    const [loader, setLoader] = useState(false);   // initial fetch
    const [saving, setSaving] = useState(false);    // save in progress
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate()
    const [user, setUser] = useState({
        a_email: "",
        a_name: "",
        a_state: "",
        a_city: "",
        a_address: "",
        a_phone: "",
    });

    const handleInputChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value })
    }

    const handleEditClick = async () => {
        if (isEditing) {
            setIsEditing(!isEditing);
            setSaving(true)
            try {
                const body = {
                    a_name: user?.a_name,
                    a_state: user?.a_state,
                    a_city: user?.a_city,
                    a_phone: user?.a_phone,
                    a_address: user?.a_address
                }
                const resp = await Calling.AdminProfileUpdate(body);
                const { status, data, message } = resp;
                if (status) {
                    setUser(data);
                    toast.success(message);
                } else {
                    toast.error(message);
                }
            } catch (error) {
                navigate('/Error');
            } finally {
                setSaving(false)
            }
        } else { setIsEditing(!isEditing); }

    };

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true)
            try {
                const resp = await Calling.GetAdminData();
                const { status, data, message } = resp;
                if (status) {
                    setUser(data);
                } else {
                    toast.error(message);
                }
            } catch (error) {
                navigate('/Error');
            } finally {
                setLoader(false)
            }
        }
        fetchData()
        // eslint-disable-next-line
    }, [])

    const fieldSx = {
        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
    };

    // Plain render fn (not a component) → no remount / focus loss while typing
    const renderField = ({ icon: Icon, label, name, value, disabled = false, colSpan = false }) => (
        <div className={colSpan ? 'md:col-span-2' : ''}>
            <TextField
                label={label}
                name={name}
                value={value || ''}
                onChange={handleInputChange}
                fullWidth
                disabled={disabled}
                sx={fieldSx}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icon size={16} color="#888B91" />
                            </InputAdornment>
                        ),
                    },
                    // readOnly lives on the native <input> → typing works in edit mode
                    htmlInput: {
                        readOnly: !isEditing || disabled,
                        ...(name === 'a_phone' ? { maxLength: 11, minLength: 9 } : {}),
                    },
                }}
            />
        </div>
    );

    // Shimmer skeleton while initial profile data loads
    if (loader) {
        return (
            <div className="w-full min-h-screen">
                <Skeleton variant="text" width={180} height={40} />
                <Skeleton variant="text" width={260} height={20} sx={{ mb: 4 }} />
                <div className="flex flex-col lg:flex-row gap-6">
                    <div className="w-full lg:w-80 shrink-0">
                        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />
                    </div>
                    <div className="flex-1">
                        <Skeleton variant="rounded" height={420} sx={{ borderRadius: '16px' }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen text-main-font">
            {/* Page Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mb-8"
            >
                <h1 className="text-2xl font-bold text-main-font">My Profile</h1>
                <p className="text-sm text-main-subfont mt-1">Manage your account information</p>
            </motion.div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Profile Card */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 }}
                    className="w-full lg:w-80 shrink-0"
                >
                    <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                        {/* Header Background */}
                        <div className="h-24 bg-gradient-to-r from-main-blue to-emerald-500 relative">
                            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                                <div className="w-24 h-24 rounded-2xl bg-main-white p-2 shadow-lg border-4 border-main-white">
                                    <img
                                        src={img1}
                                        alt="Profile"
                                        className="w-full h-full object-contain rounded-xl"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Profile Info */}
                        <div className="pt-16 pb-6 px-6 text-center">
                            <h2 className="text-xl font-bold text-main-font">{user?.a_name || 'Admin'}</h2>
                            <p className="text-sm text-main-subfont mt-1">{user?.a_email}</p>

                            <div className="mt-6 pt-6 border-t border-main-border">
                                <div className="flex items-center justify-center gap-2 text-main-subfont">
                                    <MapPin size={16} />
                                    <span className="text-sm">
                                        {user?.a_city && user?.a_state
                                            ? `${user.a_city}, ${user.a_state}`
                                            : 'Location not set'}
                                    </span>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-xl bg-main-th">
                                    <div className="text-lg font-bold text-main-blue">Admin</div>
                                    <div className="text-xs text-main-subfont">Role</div>
                                </div>
                                <div className="p-3 rounded-xl bg-blue-50">
                                    <div className="text-lg font-bold text-blue-600">Active</div>
                                    <div className="text-xs text-main-subfont">Status</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Details Card */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="flex-1"
                >
                    <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                        {/* Card Header */}
                        <div className="p-6 border-b border-main-border flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-main-font">Account Details</h3>
                                <p className="text-sm text-main-subfont mt-0.5">
                                    {isEditing ? 'Edit your information below' : 'View and manage your profile'}
                                </p>
                            </div>
                            {isEditing && (
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-main-th text-main-blue">
                                    Editing
                                </span>
                            )}
                        </div>

                        {/* Form Fields */}
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {renderField({ icon: Mail, label: "Email Address", name: "a_email", value: user?.a_email, disabled: true })}
                                {renderField({ icon: User, label: "Full Name", name: "a_name", value: user?.a_name })}
                                {renderField({ icon: Map, label: "State", name: "a_state", value: user?.a_state })}
                                {renderField({ icon: MapPin, label: "City", name: "a_city", value: user?.a_city })}
                                {renderField({ icon: Phone, label: "Phone Number", name: "a_phone", value: user?.a_phone })}
                                {renderField({ icon: Home, label: "Address", name: "a_address", value: user?.a_address, colSpan: true })}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-6 py-4 bg-main-bg border-t border-main-border flex justify-end gap-3">
                            {isEditing && (
                                <button
                                    onClick={() => setIsEditing(false)}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-main-font bg-main-white border border-main-border hover:bg-main-th transition-colors disabled:opacity-50"
                                >
                                    <X size={18} />
                                    Cancel
                                </button>
                            )}
                            <motion.button
                                whileTap={{ scale: 0.96 }}
                                onClick={handleEditClick}
                                disabled={saving}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-main-white transition-all disabled:opacity-70 disabled:cursor-not-allowed
                                    ${isEditing
                                        ? 'bg-main-blue hover:bg-main-blue/90'
                                        : 'bg-blue-500 hover:bg-blue-600'
                                    }`}
                            >
                                {saving ? (
                                    <>
                                        <CircularProgress size={16} color="inherit" />
                                        Saving...
                                    </>
                                ) : isEditing ? (
                                    <>
                                        <Save size={18} />
                                        Save Changes
                                    </>
                                ) : (
                                    <>
                                        <Pencil size={18} />
                                        Edit Profile
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </div>

                    {/* Info Note */}
                    <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100">
                        <p className="text-sm text-blue-700">
                            <span className="font-medium">Note:</span> Email address cannot be changed. Contact support if you need to update your email.
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}

export default Profile;
